import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../Components/shared/Button";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { format } from "date-fns";
import PageHeader from "../../../Components/PageHeader";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../../../utils/axiosInstance";

const AppointmentReport = () => {
  const [doctor, setDoctor] = useState("");
  const [status, setStatus] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        from: format(dateRange[0].startDate, "yyyy-MM-dd"),
        to: format(dateRange[0].endDate, "yyyy-MM-dd"),
      };

      if (doctor) params.doctorId = doctor;
      if (status) params.status = status;

      const res = await axiosInstance.get("/bookings/report");

      setReportData(res.data.bookings || res.data); // adjust if wrapped
    } catch (err) {
      console.error("Error fetching report:", err.message);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, doctor, status]);

  useEffect(() => {
    handleGenerateReport(); // Load initial data
  }, [handleGenerateReport]);

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const rangeStr = `${format(
      dateRange[0].startDate,
      "dd MMM yyyy"
    )} - ${format(dateRange[0].endDate, "dd MMM yyyy")}`;

    doc.setFontSize(16);
    doc.text("Appointment Report", 14, 15);
    doc.setFontSize(11);
    doc.text(`Date Range: ${rangeStr}`, 14, 22);

    autoTable(doc, {
      startY: 30,
      head: [
        [
          "Date",
          "Time / Token",
          "Doctor",
          "Department",
          "Patient",
          "Phone",
          "Status",
        ],
      ],
      body: reportData.map((item) => [
        item.bookingDate || "-",
        item.timeSlot || "-",
        item.doctorName || "-",
        item.departmentName || "-",
        item.patientName || "-",
        item.patientPhoneNumber || "-",
        item.status || "-",
      ]),
    });

    doc.save(`appointment-report-${rangeStr}.pdf`);
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Appointment Report"
        subtitle="Generate and download appointment reports with filters"
        showBackButton={true}
      />

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Doctor Select */}
        <div>
          <label className="block text-sm text-gray-600 mb-1">Doctor</label>
          <select
            className="w-full p-2 rounded border border-gray-300"
            value={doctor}
            onChange={(e) => setDoctor(e.target.value)}
          >
            <option value="">All Doctors</option>
            <option value="1">Dr. A</option>
            <option value="2">Dr. B</option>
          </select>
        </div>

        {/* Status Select */}
        <div>
          <label className="block text-sm text-gray-600 mb-1">Status</label>
          <select
            className="w-full p-2 rounded border border-gray-300"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="booked">Booked</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Date Range and Buttons */}
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-600 mb-1">
            Date Range & Actions
          </label>
          <div className="flex flex-col md:flex-row gap-3 md:items-end">
            {/* Date Picker */}
            <div className="relative w-full md:w-64">
              <Button
                variant="outline"
                className="w-full text-left"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                {`${format(dateRange[0].startDate, "dd MMM yyyy")} - ${format(
                  dateRange[0].endDate,
                  "dd MMM yyyy"
                )}`}
              </Button>

              {showDatePicker && (
                <div className="absolute z-50 mt-2">
                  <DateRange
                    editableDateInputs={true}
                    onChange={(item) => setDateRange([item.selection])}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRange}
                  />
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-2 w-full md:w-auto">
              <Button
                variant="primary"
                className="w-full md:w-auto whitespace-nowrap"
                onClick={handleGenerateReport}
              >
                Apply Filters
              </Button>
              <Button
                variant="outline"
                className="w-full md:w-auto whitespace-nowrap"
                onClick={handleDownloadPDF}
                disabled={reportData.length === 0}
              >
                ⬇️ Download PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm text-left border-collapse">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 border">Date</th>
              <th className="px-4 py-2 border">Time / Token</th>
              <th className="px-4 py-2 border">Doctor</th>
              <th className="px-4 py-2 border">Department</th>
              <th className="px-4 py-2 border">Patient</th>
              <th className="px-4 py-2 border">Phone</th>
              <th className="px-4 py-2 border">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-4 text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : reportData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-4 text-gray-500">
                  No data found.
                </td>
              </tr>
            ) : (
              reportData.map((item, index) => (
                <tr key={index} className="border-t">
                  <td className="px-4 py-2 border">
                    {item.bookingDate || "-"}
                  </td>
                  <td className="px-4 py-2 border">{item.timeSlot || "-"}</td>
                  <td className="px-4 py-2 border">{item.doctorName || "-"}</td>
                  <td className="px-4 py-2 border">
                    {item.departmentName || "-"}
                  </td>
                  <td className="px-4 py-2 border">
                    {item.patientName || "-"}
                  </td>
                  <td className="px-4 py-2 border">
                    {item.patientPhoneNumber || "-"}
                  </td>
                  <td className="px-4 py-2 border capitalize">
                    {item.status || "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AppointmentReport;
